import { z } from 'zod';
import { IsoDateTime } from './common.js';
import { Location } from './devices.js';

/** The longest range one history request may cover. */
export const HISTORY_MAX_RANGE_DAYS = 31;
/** Routes with more fixes than this are simplified before sending. */
export const HISTORY_DEFAULT_MAX_POINTS = 2000;
export const HISTORY_MAX_POINTS = 5000;

/** GET /devices/:id/locations?from&to[&maxPoints] */
export const LocationHistoryQuery = z
  .object({
    from: IsoDateTime,
    to: IsoDateTime,
    maxPoints: z.coerce.number().int().min(2).max(HISTORY_MAX_POINTS).default(HISTORY_DEFAULT_MAX_POINTS),
  })
  .refine((q) => Date.parse(q.to) > Date.parse(q.from), {
    message: '"to" must be after "from".',
    path: ['to'],
  })
  // 31 UK days can include the 25-hour day the clocks go back, hence the extra hour.
  .refine(
    (q) => Date.parse(q.to) - Date.parse(q.from) <= (HISTORY_MAX_RANGE_DAYS * 24 + 1) * 60 * 60 * 1000,
    {
      message: `Choose a range of ${HISTORY_MAX_RANGE_DAYS} days or less.`,
      path: ['to'],
    },
  );
export type LocationHistoryQuery = z.input<typeof LocationHistoryQuery>;

export const LocationHistoryResponse = z.object({
  /** Oldest first. */
  locations: z.array(Location),
  /** How many fixes the range holds; more than `locations.length` when simplified. */
  total: z.number().int(),
  /**
   * True when the route had more than maxPoints fixes and was simplified: points that
   * lie on a nearly straight line between their neighbours were left out, so the shape
   * of the route is kept.
   */
  simplified: z.boolean(),
  /** Distance travelled, from every fix in the range except approximate (IP-based) ones. */
  distanceM: z.number(),
});
export type LocationHistoryResponse = z.infer<typeof LocationHistoryResponse>;

/** GET /export?format=json|gpx[&device=] */
export const ExportFormat = z.enum(['json', 'gpx']);
export type ExportFormat = z.infer<typeof ExportFormat>;
export const ExportQuery = z.object({
  format: ExportFormat.default('json'),
  device: z.uuid().optional(),
});
